/*
 * Copyright 2026 North Carolina State University
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventIcon from '@mui/icons-material/Event';
import { toast } from 'react-toastify';
import { ipmApi, errorMessage } from '../api';
import { formFields, formatValue, nowHHMM } from '../fieldTypes';
import EntryDetail from '../components/EntryDetail';

const fmtDate = (iso) => formatValue({ type: 'date' }, iso);
const fmtTime = (hhmm) => formatValue({ type: 'time' }, hhmm);

/** overdue / today / upcoming, for colouring the next-check chip. */
const checkState = (date, time, today) => {
  if (!date) return null;
  if (date < today) return 'overdue';
  if (date > today) return 'upcoming';
  return time && time < nowHHMM() ? 'overdue' : 'today';
};

const CHECK_CHIP = {
  overdue: { color: 'error', prefix: 'Overdue · ' },
  today: { color: 'warning', prefix: 'Today · ' },
  upcoming: { color: 'default', prefix: '' },
};

const GroupCard = ({ group, column, today, onOpen }) => {
  const { form } = column;
  const cfg = form.summary;
  const byId = Object.fromEntries(formFields(form).map(f => [f.id, f]));
  const state = checkState(group.next_date, group.next_time, today);
  const dateOf = (e) => {
    const raw = form.entry_date_field && e.data?.[form.entry_date_field];
    return raw ? fmtDate(raw) : new Date(e.entry_date).toLocaleDateString();
  };

  return (
    <Paper variant="outlined" className="ipm-summary-card">
      <Box className="ipm-summary-card-head">
        <strong>{group.name}</strong>
        {state && (
          <Chip size="small" icon={<EventIcon />} color={CHECK_CHIP[state].color}
            variant={state === 'upcoming' ? 'outlined' : 'filled'}
            label={`${CHECK_CHIP[state].prefix}Next check ${fmtDate(group.next_date)}${group.next_time ? ` ${fmtTime(group.next_time)}` : ''}`} />
        )}
      </Box>
      {cfg.mode === 'window' && (
        <Typography variant="body2" className="ipm-muted">
          Since {fmtDate(group.start_date)} · {group.end_date ? `until ${fmtDate(group.end_date)}` : 'no end date set'}
        </Typography>
      )}
      <ul className="ipm-summary-entries">
        {group.entries.map(e => {
          const details = (cfg.display_fields || [])
            .map(id => byId[id] && formatValue(byId[id], e.data?.[id]))
            .filter(Boolean);
          return (
            <li key={e._id}>
              <ButtonBase className="ipm-summary-entry" onClick={() => onOpen(e)}>
                <span className="ipm-summary-entry-date">{dateOf(e)}</span>
                <span className="ipm-summary-entry-text">
                  {details.join(' · ') || <span className="ipm-muted">Entry</span>}
                  {e.created_by?.name && <span className="ipm-muted"> — {e.created_by.name}</span>}
                </span>
              </ButtonBase>
            </li>
          );
        })}
      </ul>
    </Paper>
  );
};

/**
 * One column per form set to show here (Manage forms → Summary settings),
 * each listing what is currently ongoing, grouped by compartment or project.
 */
const IpmSummary = ({ onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // { entry, column }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await ipmApi.summary());
    } catch (err) {
      toast.error(errorMessage(err, 'Could not load the summary'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const columns = data?.columns || [];

  return (
    <div>
      <Box className="ipm-page-header ipm-builder-header">
        <Box>
          <h2>Summary</h2>
          <p>What is currently ongoing{data ? ` as of ${fmtDate(data.today)}` : ''}. Click an entry to see it in full.</p>
        </Box>
        <Tooltip title="Refresh">
          <span><IconButton aria-label="Refresh summary" onClick={load} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
      </Box>

      {loading && !data && <CircularProgress />}

      {data && columns.length === 0 && (
        <Alert severity="info" action={onNavigate && <Button color="inherit" size="small" onClick={() => onNavigate('data-entry')}>Go to Data Entry</Button>}>
          No forms are set to show here yet. In Data Entry, open <strong>Manage forms</strong> and turn on <strong>Summary settings</strong> for a form.
        </Alert>
      )}

      {columns.length > 0 && (
        <Box className="ipm-summary-grid" style={{ '--ipm-cols': columns.length }}>
          {columns.map(col => (
            <Box key={col.form._id} className="ipm-summary-column">
              <Box className="ipm-summary-column-head">
                <Typography variant="h6">{col.form.name}</Typography>
                <Chip size="small" label={`${col.groups.length} ongoing`} />
              </Box>
              {col.groups.length === 0 && <Typography className="ipm-muted" sx={{ p: 1 }}>Nothing ongoing.</Typography>}
              {col.groups.map(g => (
                <GroupCard key={g.key} group={g} column={col} today={data.today}
                  onOpen={entry => setOpen({ entry, column: col })} />
              ))}
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle>{open?.column.form.name} entry</DialogTitle>
        <DialogContent dividers>
          {open && <EntryDetail entry={open.entry} currentFieldIds={new Set(formFields(open.column.form).map(f => f.id))} />}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(null)}>Close</Button></DialogActions>
      </Dialog>
    </div>
  );
};

export default IpmSummary;
